import React from 'react';
import { Link } from '@tanstack/react-router';
import {
  FiMail,
  FiMapPin,
  FiClock,
  FiBriefcase,
  FiGlobe,
  FiInstagram,
  FiTwitter,
  FiMusic,
  FiCheck,
  FiX,
  FiFileText,
} from 'react-icons/fi';
import { FaSpotify, FaApple, FaTiktok } from 'react-icons/fa';
import type { ApplicantItem } from '../types';
import { Avatar } from '../../../ui/Avatar';
import { StatusTip } from '../../../ui/Tooltip';
import { applicantStatusTips } from '../../../../onboarding/copy';
import { TOUR, tourTarget } from '../../../../onboarding/targets';

interface ApplicantDetailPanelProps {
  applicant: ApplicantItem | null;
  onAccept?: () => void;
  onReject?: () => void;
  onCreateOffer?: () => void;
  onPickFirst?: () => void;
  existingOfferId?: string;
}

const statusBadgeStyles: Record<string, string> = {
  pending: 'bg-[#3b2a1a] text-[#FFA842]',
  accepted: 'bg-[#12381f] text-[#43E46D]',
  rejected: 'bg-[#3d1a1a] text-[#FF6B6B]',
};

export const ApplicantDetailPanel: React.FC<ApplicantDetailPanelProps> = ({
  applicant,
  onAccept,
  onReject,
  onCreateOffer,
  onPickFirst,
  existingOfferId,
}) => {
  if (!applicant) {
    return (
      <div className="flex h-full min-h-[500px] flex-col items-center justify-center rounded-3xl border border-white/10 bg-[#0A0C11] p-8 text-center">
        {/* Card Mockup Illustration with overlapping floating mail badge */}
        <div className="relative mb-6 flex h-24 w-36 items-center justify-center rounded-2xl border border-white/10 bg-[#161922]">
          <div className="w-24 space-y-2">
            <div className="h-2.5 w-16 rounded bg-white/10" />
            <div className="h-2 w-20 rounded bg-white/10" />
          </div>
          <div className="absolute -bottom-3 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-[#222736] text-white shadow-lg">
            <FiMail size={18} />
          </div>
        </div>

        <h3 className="mb-2 text-xl font-bold text-white">No applicant selected</h3>
        <p className="mb-6 max-w-xs text-sm text-zinc-400">
          Select an applicant from the list to view details and respond.
        </p>

        {onPickFirst && (
          <button
            onClick={onPickFirst}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#13151a] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-white/10 cursor-pointer"
          >
            <FiMail size={16} />
            <span>Pick an applicant to start</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative flex h-full max-h-full flex-col rounded-3xl border border-white/10 bg-[#0A0C11] p-4 sm:p-6 shadow-2xl overflow-hidden min-h-0">
      {/* Pinned Top Header Card */}
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-white/10 pb-4 mb-4">
        <div className="flex items-center gap-4 min-w-0">
          {applicant.username ? (
            <Link
              to="/profile/$identifier"
              params={{ identifier: applicant.username }}
              className="shrink-0"
            >
              <Avatar
                src={applicant.profileImage || undefined}
                name={applicant.fullName || applicant.username}
                size="lg"
                className="border border-white/15 hover:border-[#1784FF]/60 transition-colors"
              />
            </Link>
          ) : (
            <Avatar
              src={applicant.profileImage || undefined}
              name={applicant.fullName || applicant.username}
              size="lg"
              className="border border-white/15 shrink-0"
            />
          )}
          <div className="min-w-0">
            {applicant.userTypes && applicant.userTypes.length > 0 && (
              <p className="text-xs font-semibold text-[#B084F4] truncate">
                {applicant.userTypes.join(' / ')}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {applicant.username ? (
                <Link
                  to="/profile/$identifier"
                  params={{ identifier: applicant.username }}
                  className="inline-block max-w-full"
                >
                  <h2 className="text-xl font-bold text-white truncate hover:underline">
                    {applicant.fullName || applicant.username}
                  </h2>
                </Link>
              ) : (
                <h2 className="text-xl font-bold text-white truncate">
                  {applicant.fullName || applicant.username}
                </h2>
              )}
              <StatusTip content={applicantStatusTips[applicant.status]} className="shrink-0">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize shrink-0 ${
                    statusBadgeStyles[applicant.status] || 'bg-zinc-800 text-zinc-300'
                  }`}
                >
                  {applicant.status}
                </span>
              </StatusTip>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-zinc-400">
              <span className="inline-flex items-center gap-1">
                <FiMapPin size={13} className="text-[#1784FF]" />
                {applicant.location}
              </span>
              <span className="inline-flex items-center gap-1">
                <FiClock size={13} className="text-[#1784FF]" />
                {applicant.timeAgo}
              </span>
            </div>
          </div>
        </div>

        <a
          href={`mailto:${applicant.email}`}
          aria-label="Send applicant a mail"
          title="Send applicant a mail"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 p-2.5 text-xs font-semibold text-white transition hover:bg-white/10 shrink-0 sm:px-4 sm:py-2 lg:p-2.5 xl:px-4 xl:py-2"
        >
          <FiMail size={14} />
          <span className="hidden sm:inline lg:hidden xl:inline">Send applicant a mail</span>
        </a>
      </div>

      {/* Scrollable Content Body */}
      <div className="space-y-5 overflow-y-auto custom-modal-scrollbar pr-2 flex-1 min-h-0">
        {/* Applied to box */}
        <div className="flex items-center gap-3 rounded-2xl border border-white/5 bg-black/40 p-3.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-800 text-zinc-300 shrink-0">
            <FiBriefcase size={16} />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase text-zinc-500">Applied to</p>
            <p className="text-sm font-semibold text-white">{applicant.appliedToGig}</p>
          </div>
        </div>

        {/* Social Links grid */}
        <div className="grid grid-cols-2 gap-2.5">
          {[
            { icon: FaSpotify, label: 'Spotify', href: applicant.socialLinks.spotify },
            { icon: FaApple, label: 'Apple Music', href: applicant.socialLinks.appleMusic },
            { icon: FaTiktok, label: 'TikTok', href: applicant.socialLinks.tiktok },
            { icon: FiGlobe, label: 'Website', href: applicant.socialLinks.website },
            { icon: FiInstagram, label: 'Instagram', href: applicant.socialLinks.instagram },
            { icon: FiTwitter, label: 'Twitter (X)', href: applicant.socialLinks.twitter },
          ].map((social) => (
            <a
              key={social.label}
              href={social.href || undefined}
              target={social.href ? '_blank' : undefined}
              rel={social.href ? 'noreferrer' : undefined}
              className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-xs font-medium transition ${
                social.href
                  ? 'border-white/15 bg-[#12151f] text-white hover:bg-white/10'
                  : 'pointer-events-none border-white/5 bg-[#0f1118] text-zinc-600'
              }`}
            >
              <social.icon size={15} />
              <span>{social.label}</span>
            </a>
          ))}
        </div>

        {/* About section */}
        {applicant.bio && (
          <div>
            <h3 className="mb-2 text-base font-bold text-white">About</h3>
            <p className="text-xs leading-relaxed text-zinc-300">{applicant.bio}</p>
          </div>
        )}

        {/* Genres section */}
        {applicant.genres && applicant.genres.length > 0 && (
          <div>
            <h3 className="mb-3 text-base font-bold text-white">Genres</h3>
            <div className="grid grid-cols-2 gap-y-2">
              {applicant.genres.map((genre, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 text-xs font-medium text-zinc-200"
                >
                  <FiMusic size={14} className="text-[#00D45A]" />
                  <span>{genre}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Skills section */}
        {applicant.skills && applicant.skills.length > 0 && (
          <div>
            <h3 className="mb-3 text-base font-bold text-white">Skills</h3>
            <div className="grid grid-cols-2 gap-y-2">
              {applicant.skills.map((skill, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 text-xs font-medium text-zinc-300"
                >
                  <FiMusic size={14} className="text-[#E5DD3A]" />
                  <span>{skill}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Bottom Actions */}
      {applicant.status !== 'rejected' && (
        <div
          {...tourTarget(TOUR.applicantActions)}
          className="mt-4 flex shrink-0 flex-col gap-3 border-t border-white/10 pt-4 bg-[#0A0C11]"
        >
          {applicant.status === 'pending' && (
            <div className="flex items-center gap-3">
              <button
                onClick={onAccept}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-semibold text-black transition hover:bg-zinc-200 cursor-pointer"
              >
                <FiCheck size={16} className="text-emerald-600 stroke-[3]" />
                <span>Accept</span>
              </button>
              <button
                onClick={onReject}
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/20 bg-black py-3 text-sm font-semibold text-zinc-200 transition hover:bg-white/5 cursor-pointer"
              >
                <FiX size={16} className="text-red-500 stroke-[3]" />
                <span>Reject</span>
              </button>
            </div>
          )}
          {applicant.status === 'accepted' &&
            (existingOfferId ? (
              <Link
                to="/offers/$offerId"
                params={{ offerId: existingOfferId }}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-semibold text-black transition hover:bg-zinc-200 cursor-pointer"
              >
                <FiFileText size={16} />
                <span>View Offer</span>
              </Link>
            ) : (
              onCreateOffer && (
                <button
                  type="button"
                  onClick={onCreateOffer}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1784FF] py-3 text-sm font-semibold text-white transition hover:bg-blue-600 cursor-pointer"
                >
                  <FiFileText size={16} />
                  <span>Create Offer</span>
                </button>
              )
            ))}
        </div>
      )}
    </div>
  );
};
